import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Form,
  Button,
  Alert,
  Spinner,
  Container,
  Row,
  Col,
  Card,
  Modal
} from 'react-bootstrap';

/* ✅ moved outside (performance fix) */
const LabelRow = ({ label, children }) => (
  <div className="d-flex align-items-center mb-3">
    <div style={{ width: 140, fontWeight: 600, color: '#555' }}>
      {label}
    </div>
    <div style={{ flex: 1 }}>{children}</div>
  </div>
);

function EditProduct() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState({
    name: '',
    brand: '',
    category: '',
    description: '',
    price: '',
    countInStock: '',
    images: []
  });

  const [initialProduct, setInitialProduct] = useState(null);

  const [images, setImages] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalText, setModalText] = useState('Product Updated');

  /* ---------------- FETCH ---------------- */
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await axios.get(`https://apimyshop.myidealtd.co.uk/api/products/${id}`);

        const normalized = {
          name: String(res.data.name || ''),
          brand: String(res.data.brand || ''),
          category: String(res.data.category || ''),
          description: String(res.data.description || ''),
          price: String(res.data.price || ''),
          countInStock: String(res.data.countInStock || ''),
          images: Array.isArray(res.data.images) ? [...res.data.images] : []
        };

        setProduct({ ...normalized });
        setInitialProduct({ ...normalized });

        setImages(
          (res.data.images || []).map(img => ({
            type: 'existing',
            src: img
          }))
        );

      } catch (err) {
        setError('Error fetching product.');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  /* ✅ FIXED INPUT HANDLER */
  const handleChange = (e) => {
    const { name, value } = e.target;

    setProduct(prev => {
      if (prev[name] === value) return prev; // prevent unnecessary re-render
      return {
        ...prev,
        [name]: value
      };
    });
  };

  /* ---------------- FILES ---------------- */
  const handleFiles = (files) => {
    const remaining = 3 - images.length;
    const selected = Array.from(files).slice(0, remaining);

    const newImgs = selected.map(file => ({
      type: 'new',
      src: URL.createObjectURL(file),
      file
    }));

    setImages(prev => [...prev, ...newImgs]);
  };

  const handleImageChange = (e) => handleFiles(e.target.files);

  /* ---------------- DRAG ---------------- */
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    handleFiles(e.dataTransfer.files);
  };

  /* ---------------- REMOVE ---------------- */
  const removeImage = (index) => {
    const updated = [...images];
    updated.splice(index, 1);
    setImages(updated);
  };

  /* ✅ FIXED PERFORMANCE */
  const isSameProduct = useCallback(() => {
    if (!initialProduct) return false;

    const sameFields =
      product.name === initialProduct.name &&
      product.brand === initialProduct.brand &&
      product.category === initialProduct.category &&
      product.price === initialProduct.price &&
      product.countInStock === initialProduct.countInStock &&
      product.description === initialProduct.description;

    const sameImages =
      JSON.stringify(
        images.filter(i => i.type === 'existing').map(i => i.src)
      ) === JSON.stringify(initialProduct.images || []);

    return sameFields && sameImages;
  }, [product, images, initialProduct]);

  /* ---------------- UPDATE ---------------- */
  const handleUpdate = async () => {
    setUpdating(true);
    setError('');

    try {
      if (isSameProduct()) {
        setModalText('No changes detected');
        setShowModal(true);

        setTimeout(() => {
          setShowModal(false);
          navigate('/productlist', { replace: true });
        }, 1500);

        return;
      }

      const formData = new FormData();

      Object.entries(product).forEach(([k, v]) => {
        if (k !== 'images') formData.append(k, v);
      });

      const existingImages = images
        .filter(i => i.type === 'existing')
        .map(i => i.src);

      formData.append('existingImages', JSON.stringify(existingImages));

      images.forEach(img => {
        if (img.type === 'new') {
          formData.append('photos', img.file);
        }
      });

      await axios.put(
        `https://apimyshop.myidealtd.co.uk/api/products/update/${id}`,
        formData,
        { withCredentials: true }
      );

      setModalText('Product Updated');
      setShowModal(true);

      setTimeout(() => {
        setShowModal(false);
        navigate('/productlist', { replace: true });
      }, 1500);

    } catch (err) {
      setError(err.response?.data?.message || 'Error updating product.');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <Container className="py-5" style={{ maxWidth: 1200 }}>
      <h2 className="fw-bold mb-4">Edit Product</h2>

      {error && <Alert variant="danger">{error}</Alert>}

      <Form>
        <Row className="g-4">

          {/* LEFT */}
          <Col md={6}>
            <Card className="p-4 border-0 shadow-sm rounded-4">
              <h5 className="mb-4">Product Details</h5>

              <LabelRow label="Product Name">
                <Form.Control name="name" value={product.name} onChange={handleChange} />
              </LabelRow>

              <LabelRow label="Brand">
                <Form.Control name="brand" value={product.brand} onChange={handleChange} />
              </LabelRow>

              <LabelRow label="Category">
                <Form.Control name="category" value={product.category} onChange={handleChange} />
              </LabelRow>

              <LabelRow label="Price">
                <Form.Control type="number" name="price" value={product.price} onChange={handleChange} />
              </LabelRow>

              <LabelRow label="Stock">
                <Form.Control type="number" name="countInStock" value={product.countInStock} onChange={handleChange} />
              </LabelRow>

              <LabelRow label="Description">
                <Form.Control as="textarea" rows={4} name="description" value={product.description} onChange={handleChange} />
              </LabelRow>
            </Card>
          </Col>

          {/* RIGHT */}
          <Col md={6}>
            <Card className="p-4 border-0 shadow-sm rounded-4">

              <div className="d-flex justify-content-between mb-3">
                <h5>Product Images</h5>
                <span className="text-muted">{images.length}/3</span>
              </div>

              <div className="d-flex flex-wrap gap-3 mb-4">

                {images.map((img, index) => (
                  <div key={index} style={{ width: 130 }}>
                    <div style={{ borderRadius: 16, overflow: 'hidden', border: '1px solid #eee' }}>
                      <img
                        src={
                          img.type === 'existing'
                            ? `https://apimyshop.myidealtd.co.uk/uploads/${img.src}`
                            : img.src
                        }
                        onClick={() => setPreviewIndex(index)}
                        style={{
                          width: '100%',
                          height: 120,
                          objectFit: 'cover',
                          cursor: 'pointer'
                        }}
                      />
                    </div>

                    <div className="text-center mt-2">
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => removeImage(index)}
                        style={{ borderRadius: 10, fontSize: 12 }}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}

              </div>

              <div
                onDragEnter={handleDrag}
                onDragOver={handleDrag}
                onDragLeave={handleDrag}
                onDrop={handleDrop}
                style={{
                  border: dragActive ? '2px solid #0d6efd' : '2px dashed #ccc',
                  borderRadius: 16,
                  padding: 30,
                  textAlign: 'center',
                  background: dragActive ? '#f0f7ff' : '#fafafa'
                }}
              >
                <p className="fw-semibold mb-1">Drag & drop images</p>
                <small className="text-muted">or click to upload</small>

                <Form.Control
                  type="file"
                  multiple
                  className="mt-3"
                  onChange={handleImageChange}
                />
              </div>

            </Card>
          </Col>

        </Row>

        <div className="text-end mt-4">
          <Button size="lg" disabled={updating} onClick={handleUpdate}>
            {updating ? 'Updating...' : 'Save Changes'}
          </Button>
        </div>
      </Form>

      <Modal show={showModal} centered>
        <Modal.Body className="text-center p-4">
          <h5>{modalText}</h5>
          <small className="text-muted">Redirecting...</small>
        </Modal.Body>
      </Modal>

    </Container>
  );
}

export default EditProduct;